import * as React from 'react';
import { FC } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { Formik } from 'formik';

import ActionButton from 'components/action-button';
import List, { ListItem } from 'components/list';
import LoadingAnimation from 'components/loading-animation';
import { ALERT_STYLE, SupportAlertBox } from 'components/support-alert-box';
import { FormStatus } from 'enums/support-form';
import { PageHeading, PageWrap, StyledBackButton } from 'pages/report-order-issue/styled';
import noop from 'utils/noop';

import { CenteredP, CommentTextareaInput, ItemNameText, Spacing } from './styled';
import { IReportOrderNeverArrivedFormData, IReportOrderNeverArrivedPageViewProps } from './types';

export const ReportOrderNeverArrivedPageView: FC<IReportOrderNeverArrivedPageViewProps> = ({
  headingText = 'Heading',
  subheadingText = 'Subheading',
  contentText = 'Content',
  commentsPlaceholderText = 'Comments',
  submitButtonText = 'Submit',
  refundErrorText = 'failed',
  refundSuccessText = 'success',
  isLoading = false,
  items = [],
  formStatus = FormStatus.UNSENT,
  onBack = noop,
  onSubmit = noop,
  disableSubmitButton,
}) => {
  const handleFormikSubmit = (values: IReportOrderNeverArrivedFormData) => onSubmit(values);

  return (
    <PageWrap>
      <PageHeading as="h1">{headingText}</PageHeading>
      <StyledBackButton onClick={onBack} />
      <Spacing x={2} y={1}>
        <CenteredP>
          <strong>{subheadingText}</strong>
          <br />
          {contentText}
        </CenteredP>
      </Spacing>
      <List data-testid="report-order-missing-items">
        {items.map(item => (
          <ListItem key={item.id} data-testid="report-order-missing-item">
            <Spacing x={6} y={1}>
              <ItemNameText>
                {item.quantity}x {item.name}
              </ItemNameText>
            </Spacing>
          </ListItem>
        ))}
      </List>
      {!items.length ? (
        <LoadingAnimation fillColor={Styles.color.primary} />
      ) : (
        <Formik<IReportOrderNeverArrivedFormData>
          initialValues={{
            comments: '',
          }}
          onSubmit={handleFormikSubmit}
        >
          {formikProps => (
            <form onSubmit={formikProps.handleSubmit}>
              <Spacing x={4}>
                <Spacing y={1}>
                  <CommentTextareaInput
                    name="comments"
                    value={formikProps.values.comments}
                    onChange={formikProps.handleChange}
                    placeholder={`${commentsPlaceholderText}:`}
                    data-testid="report-order-missing-comments-field"
                  />
                </Spacing>
                <Spacing y={1}>
                  <Box center width="100%">
                    <ActionButton
                      type="submit"
                      disabled={disableSubmitButton || formikProps.isSubmitting}
                      data-testid="report-order-missing-submit"
                      isLoading={isLoading}
                    >
                      {submitButtonText}
                    </ActionButton>
                  </Box>
                </Spacing>
                {formStatus === FormStatus.SUCCESSFUL_REFUND && (
                  <SupportAlertBox alertStyle={ALERT_STYLE.success} testId="report-order-success">
                    {refundSuccessText}
                  </SupportAlertBox>
                )}
                {formStatus === FormStatus.FAILED_REFUND && (
                  <SupportAlertBox alertStyle={ALERT_STYLE.error} testId="report-order-failed">
                    {refundErrorText}
                  </SupportAlertBox>
                )}
              </Spacing>
            </form>
          )}
        </Formik>
      )}
    </PageWrap>
  );
};

export default ReportOrderNeverArrivedPageView;
